/**
 * useRedemption — fetches "what loyalty rewards can this customer redeem
 * against the current order?" by calling
 * `POST /v1/online-ordering/redemption-options` with a draft order body.
 *
 * Mirrors v1's `check-out.page.ts > redemptionOptions()` plus the
 * `prepareLoyaltyData()` helper that injects the chosen redemption back
 * into the order payload at create time.
 */

import { useEffect, useMemo, useState } from "react";

import { loyaltyApi, type RedemptionOption } from "@/api/services";

export type UseRedemptionInput = {
  /** Draft order body sent to redemption-options. Pass `null` to disable. */
  orderDraft: unknown | null;
  /** Disable the call when the user is not signed in (avoids 401 noise). */
  enabled: boolean;
};

export type UseRedemptionResult = {
  /** First / best available option, or null when nothing is redeemable. */
  option: RedemptionOption | null;
  /** Currency value the toggle would save the customer. */
  redeemableValue: number;
  /** True while the option is being fetched. */
  loading: boolean;
  /** True when the toggle should be greyed out (no eligible redemption). */
  disabled: boolean;
  error: Error | null;
};

export function useRedemption(input: UseRedemptionInput): UseRedemptionResult {
  const { orderDraft, enabled } = input;
  const [option, setOption] = useState<RedemptionOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Cheap stable key so we don't refetch on every render. Falls back to a
  // JSON serialise (orderDraft is typically small).
  const draftKey = useMemo(() => {
    if (!orderDraft || !enabled) return null;
    try {
      return JSON.stringify(orderDraft);
    } catch {
      return null;
    }
  }, [orderDraft, enabled]);

  useEffect(() => {
    if (!enabled || !draftKey || !orderDraft) {
      setOption(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const list = await loyaltyApi.redemptionOptions(orderDraft);
        if (cancelled) return;
        setOption(list?.[0] ?? null);
      } catch (err) {
        if (cancelled) return;
        setOption(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftKey, enabled, orderDraft]);

  const redeemableValue =
    typeof option?.redeemableValue === "number" ? option.redeemableValue : 0;
  const disabled = !option || redeemableValue <= 0;

  return { option, redeemableValue, loading, disabled, error };
}
